// Unified traffic-target shape. Every source (adsb.fi internet poller,
// future GDL90 receiver, future Garmin Connext) normalizes its native
// payload to this interface so the map renderer and detail card don't
// have to know which source a target came from.
//
// Field semantics match GDL90 Traffic Report / Ownship Report (FAA
// public ICD) where possible, with adsb.fi's richer metadata (callsign
// / registration / aircraft type / operator) carried alongside.

export type TrafficSource = 'internet' | 'gdl90' | 'connext';

/**
 * Emergency / priority code per GDL90 §3.5.1.10 + ADS-B Emergency
 * Status field. None of these should ever filter a target out — we
 * always show every aircraft — but they up-tier the icon color so a
 * MEDEVAC / hijack / minimum-fuel target stands out.
 */
export type TrafficEmergency =
  | 'none'
  | 'general'
  | 'lifeguard'   // medical / MEDEVAC
  | 'minfuel'
  | 'nordo'       // no radio
  | 'unlawful'    // hijack
  | 'downed'
  | 'reserved';

export interface TrafficTarget {
  /** ICAO 24-bit address, lowercase hex. Stable across feeds, our
   *  primary key in the store. */
  icaoHex: string;
  /** Best-effort callsign (often padded with spaces from the wire;
   *  caller has trimmed). */
  callsign: string | null;
  /** Tail number / registration when known. */
  registration: string | null;
  /** ICAO type designator, e.g. 'B738', 'BH06', 'AS50'. */
  typeIcao: string | null;
  /** Human-readable type description, e.g. 'Boeing 737-800'. */
  typeDescription: string | null;
  /** Owner / operator string when surfaced upstream. */
  operator: string | null;

  /** Position, WGS-84 degrees. */
  lat: number;
  lon: number;

  /** Pressure altitude in feet (preferred for traffic separation). */
  altBaroFt: number | null;
  /** Geometric (GPS) altitude in feet. */
  altGeomFt: number | null;
  /** Groundspeed in knots. */
  groundspeedKt: number | null;
  /** True track in degrees (0-359). */
  trackDeg: number | null;
  /** Vertical rate in feet per minute (negative = descending). */
  vertRateFpm: number | null;

  /** ICAO Mode-S emitter category (A1=light, A3=large, B1=glider, ...). */
  category: string | null;
  /** ATC squawk (4-digit octal). */
  squawk: string | null;
  emergency: TrafficEmergency;
  /** True when the target is military-marked upstream. We do NOT
   *  filter on this; it's surfaced for the badge in the detail card. */
  military: boolean;

  /**
   * Epoch millis when this target was last observed at this position.
   * The store's priority resolver uses this + the source's freshness
   * window to decide which source's copy of a duplicate wins.
   */
  lastSeenMs: number;
  /** Origin source — drives the INET / GDL90 chip in the UI. */
  source: TrafficSource;
}

/**
 * Source-resolution metadata. Per-source priority + freshness window.
 * Sources defined here can write into useTrafficStore.targets; the
 * store re-runs `resolvePriority()` on every push and keeps only the
 * freshest copy per ICAO.
 *
 * Lower `priority` wins ties. `freshnessMs` is the window past which
 * a target from this source is considered stale and is dropped from
 * the resolver (the source can re-publish to revive it).
 */
export interface SourceConfig {
  source: TrafficSource;
  /** 0 = highest priority. GDL90 receiver beats internet by design. */
  priority: number;
  freshnessMs: number;
}

export const SOURCE_CONFIGS: Readonly<Record<TrafficSource, SourceConfig>> = {
  gdl90:    { source: 'gdl90',    priority: 0, freshnessMs:  3_000 },
  connext:  { source: 'connext',  priority: 0, freshnessMs:  3_000 },
  internet: { source: 'internet', priority: 1, freshnessMs: 45_000 },
};
